import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, X, Upload, FileText, Download } from "lucide-react";

export default function AdminClientPlan() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState(null);
  const [practices, setPractices] = useState([]);
  const [videos, setVideos] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileDescription, setFileDescription] = useState("");
  const [goalInput, setGoalInput] = useState("");
  const [noteInput, setNoteInput] = useState({ date: new Date().toISOString().slice(0, 10), title: "", note: "" });
  const [saving, setSaving] = useState(false);

  const loadFiles = async () => {
    const r = await api.get(`/admin/clients/${userId}/files`);
    setFiles(r.data);
  };

  useEffect(() => {
    const load = async () => {
      const [pl, pr, vd, fl] = await Promise.all([
        api.get(`/admin/plans/${userId}`),
        api.get("/practices"),
        api.get("/videos"),
        api.get(`/admin/clients/${userId}/files`),
      ]);
      setUser(pl.data.user);
      setPlan(pl.data.plan);
      setPractices(pr.data);
      setVideos(vd.data);
      setFiles(fl.data);
    };
    load().catch((e) => toast.error(formatApiError(e)));
  }, [userId]);

  if (!plan || !user) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading...</div>;

  const togglePractice = (id) => {
    const cur = plan.assigned_practice_ids || [];
    setPlan({ ...plan, assigned_practice_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const toggleVideo = (id) => {
    const cur = plan.assigned_video_ids || [];
    setPlan({ ...plan, assigned_video_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const addGoal = () => {
    const g = goalInput.trim();
    if (!g) return;
    setPlan({ ...plan, goals: [...(plan.goals || []), g] });
    setGoalInput("");
  };
  const removeGoal = (idx) => {
    setPlan({ ...plan, goals: plan.goals.filter((_, i) => i !== idx) });
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const payload = {
        welcome_message: plan.welcome_message || "",
        coach_notes: plan.coach_notes || "",
        goals: plan.goals || [],
        weekly_session_target: Number(plan.weekly_session_target) || 0,
        assigned_practice_ids: plan.assigned_practice_ids || [],
        assigned_video_ids: plan.assigned_video_ids || [],
      };
      const r = await api.put(`/admin/plans/${userId}`, payload);
      setPlan(r.data);
      toast.success(`Plan saved for ${user.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteInput.note.trim()) {
      toast.error("Note body is required");
      return;
    }
    try {
      const r = await api.post(`/admin/plans/${userId}/session-notes`, noteInput);
      setPlan({ ...plan, session_notes: [r.data, ...(plan.session_notes || [])] });
      setNoteInput({ date: new Date().toISOString().slice(0, 10), title: "", note: "" });
      toast.success("Note added");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/plans/${userId}/session-notes/${noteId}`);
      setPlan({ ...plan, session_notes: plan.session_notes.filter((n) => n.id !== noteId) });
      toast.success("Note deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onFileUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit");
      e.target.value = "";
      return;
    }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("description", fileDescription);
      await api.post(`/admin/clients/${userId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${f.name}`);
      setFileDescription("");
      await loadFiles();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingFile(false);
      e.target.value = "";
    }
  };

  const deleteFile = async (fileId, name) => {
    if (!window.confirm(`Delete "${name}"? The client will no longer see it.`)) return;
    try {
      await api.delete(`/admin/files/${fileId}`);
      setFiles(files.filter((f) => f.id !== fileId));
      toast.success("File removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadFile = (fileId) => {
    const token = localStorage.getItem("epb_token");
    const url = `${api.defaults.baseURL}/files/${fileId}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
      <button onClick={() => navigate("/admin")} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1" data-testid="plan-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </button>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-primary mb-1">Editing plan</p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">{user.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user.email} · joined {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
        <Button onClick={savePlan} disabled={saving} className="rounded-full" data-testid="plan-save-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving..." : "Save plan"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* Welcome + coach notes + target */}
        <Card className="lg:col-span-2 p-7 space-y-6">
          <div>
            <Label>Personalised welcome message</Label>
            <Textarea value={plan.welcome_message || ""} onChange={(e) => setPlan({ ...plan, welcome_message: e.target.value })}
              rows={3} placeholder="e.g. Welcome back, Sarah — here's your focus area for this month." data-testid="plan-welcome-input" />
          </div>
          <div>
            <Label>Coach notes (visible to client)</Label>
            <Textarea value={plan.coach_notes || ""} onChange={(e) => setPlan({ ...plan, coach_notes: e.target.value })}
              rows={6} placeholder="Private notes for this client — observations, recommendations, reminders." data-testid="plan-coach-notes-input" />
          </div>
        </Card>

        {/* Goals + target */}
        <Card className="p-7 space-y-6">
          <div>
            <Label>Weekly session target</Label>
            <Input type="number" min={0} max={50} value={plan.weekly_session_target || 0}
              onChange={(e) => setPlan({ ...plan, weekly_session_target: e.target.value })} data-testid="plan-target-input" />
          </div>
          <div>
            <Label>Focus areas / goals</Label>
            <div className="flex gap-2 mt-2">
              <Input value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGoal(); } }}
                placeholder="Add a focus area" data-testid="plan-goal-input" />
              <Button type="button" variant="outline" size="sm" onClick={addGoal} data-testid="plan-goal-add-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="plan-goals-list">
              {(plan.goals || []).map((g, i) => (
                <Badge key={i} variant="secondary" className="pl-3 pr-1 py-1 text-sm flex items-center gap-1">
                  {g}
                  <button onClick={() => removeGoal(i)} className="ml-1 hover:text-destructive" data-testid={`plan-goal-remove-${i}`}><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              {(plan.goals || []).length === 0 && <p className="text-xs text-muted-foreground">No goals yet.</p>}
            </div>
          </div>
        </Card>
      </div>

      {/* Assigned practices */}
      <Card className="p-7 mt-6">
        <h2 className="font-display text-lg font-semibold tracking-tight mb-1">Assigned practices</h2>
        <p className="text-xs text-muted-foreground mb-4">Tick the practices you want this client to focus on. They appear on their "My Plan" page as their prescription.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plan-practices-grid">
          {practices.map((p) => {
            const checked = (plan.assigned_practice_ids || []).includes(p.id);
            return (
              <label key={p.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-primary bg-secondary/30" : "border-border hover:border-primary/40"}`}>
                <Checkbox checked={checked} onCheckedChange={() => togglePractice(p.id)} data-testid={`plan-practice-${p.id}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.title}</p>
                  <p className="text-xs text-muted-foreground">{p.category} · {p.duration_minutes} min</p>
                </div>
              </label>
            );
          })}
        </div>
      </Card>

      {/* Assigned videos */}
      <Card className="p-7 mt-6">
        <h2 className="font-display text-lg font-semibold tracking-tight mb-1">Assigned videos</h2>
        <p className="text-xs text-muted-foreground mb-4">Tick the videos you want to recommend to this specific client.</p>
        {videos.length === 0 ? (
          <p className="text-sm text-muted-foreground">No videos uploaded yet — add some from Admin → Videos.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="plan-videos-grid">
            {videos.map((v) => {
              const checked = (plan.assigned_video_ids || []).includes(v.id);
              return (
                <label key={v.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? "border-primary bg-secondary/30" : "border-border hover:border-primary/40"}`}>
                  <Checkbox checked={checked} onCheckedChange={() => toggleVideo(v.id)} data-testid={`plan-video-${v.id}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{v.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{v.category || "General"}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </Card>

      {/* Session notes */}
      <Card className="p-7 mt-6">
        <h2 className="font-display text-lg font-semibold tracking-tight mb-1">1:1 session notes</h2>
        <p className="text-xs text-muted-foreground mb-4">After each session, log a note here. It appears on the client's "My Plan" timeline.</p>
        <div className="grid sm:grid-cols-4 gap-3 mb-3">
          <div className="sm:col-span-1"><Label className="text-xs">Date</Label><Input type="date" value={noteInput.date} onChange={(e) => setNoteInput({ ...noteInput, date: e.target.value })} data-testid="plan-note-date" /></div>
          <div className="sm:col-span-3"><Label className="text-xs">Title (optional)</Label><Input value={noteInput.title} onChange={(e) => setNoteInput({ ...noteInput, title: e.target.value })} placeholder="e.g. Pre-launch debrief" data-testid="plan-note-title" /></div>
        </div>
        <Label className="text-xs">Note</Label>
        <Textarea rows={4} value={noteInput.note} onChange={(e) => setNoteInput({ ...noteInput, note: e.target.value })} placeholder="What you covered, what to practise this week, observations." data-testid="plan-note-body" />
        <div className="flex justify-end mt-3">
          <Button onClick={addNote} className="rounded-full" data-testid="plan-note-add-btn"><Plus className="h-4 w-4 mr-1" /> Add session note</Button>
        </div>

        <div className="mt-8 space-y-4">
          {(plan.session_notes || []).map((n) => (
            <div key={n.id} className="p-4 rounded-lg border border-border" data-testid={`plan-note-row-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">{new Date(n.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</p>
                  {n.title && <p className="font-medium mt-1">{n.title}</p>}
                  <p className="text-sm mt-2 whitespace-pre-line text-foreground/85">{n.note}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => deleteNote(n.id)} data-testid={`plan-note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          {(plan.session_notes || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No session notes yet.</p>}
        </div>
      </Card>

      {/* Shared files */}
      <Card className="p-7 mt-6" data-testid="plan-files-card">
        <h2 className="font-display text-lg font-semibold tracking-tight mb-1">Shared files</h2>
        <p className="text-xs text-muted-foreground mb-4">Upload PDFs, worksheets, recordings, or any document for this client. Visible only to them.</p>

        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Description (optional)</Label>
            <Input value={fileDescription} onChange={(e) => setFileDescription(e.target.value)} placeholder="e.g. Week 2 worksheet" data-testid="plan-file-description" />
          </div>
          <div className="flex items-end">
            <label className={`w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md border cursor-pointer transition-colors ${uploadingFile ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90 border-primary"}`} data-testid="plan-file-upload-label">
              <Upload className="h-4 w-4" />
              {uploadingFile ? "Uploading..." : "Upload file"}
              <input type="file" className="hidden" onChange={onFileUpload} disabled={uploadingFile} data-testid="plan-file-input" />
            </label>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Max 25 MB per file.</p>

        <div className="mt-6 space-y-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`plan-file-row-${f.id}`}>
              <FileText className="h-5 w-5 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{f.original_filename}</p>
                <p className="text-xs text-muted-foreground">
                  {(f.size / 1024).toFixed(1)} KB · {new Date(f.created_at).toLocaleDateString()}
                  {f.description ? ` · ${f.description}` : ""}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadFile(f.id)} data-testid={`plan-file-download-${f.id}`}>
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => deleteFile(f.id, f.original_filename)} data-testid={`plan-file-delete-${f.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          {files.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No files shared yet.</p>}
        </div>
      </Card>
    </div>
  );
}
